import CustomReactJsonSchemaFormComponents from '@bht/react-jsonschema-form';
import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Menu from './menu';
import Questionnaire from './questionnaire';
import Questionnaires from './questionnaires';
import ConfirmActionPanel from './confirmActionPanel';

import { exportHtml } from './helpers/exportHtmlHelper';

import useGetQuestionnaires from './hooks/useGetQuestionnairesHook';
import useGetQuestionnaire from './hooks/useGetQuestionnaireHook';
import useSaveDraftQuestionnaireHook from './hooks/useSaveDraftQuestionnaireHook';
import useSendQuestionnaire from './hooks/useSendQuestionnaireHook';
import useExportQuestionnaireHook from './hooks/useExportQuestionnaireHook';
import notificationTypes from '../../../shared/components/formNotification/notificationTypes';
import { scrollToNearestVerticalPageEdge } from '../../../shared/helpers/scroll.helper';
import { setSelectedElement, setSelectedMenuItem } from '../../actions';
import {
  MENU_ITEM__FORMS__ASSIGNED,
  MENU_ITEM__FORMS__SENT,
  MENU_ITEM__FORMS__DRAFT,
  MENU_ITEM__FORMS__RETURNED,
  MENU_ITEM__FORMS__APPROVED,
  MENU_ITEM__QUESTIONNAIRES__AVAILABLE,
} from '../../constants';
import {
  mapQuestionnaires,
  prepareQuestionnaireToSave,
} from './helpers/formDataHelper';
import { convertToReactJsonSchemaFormData } from './helpers/convertToReactJsonSchemaFormData';
import initializeReactJsonSchemaFormData from './helpers/initializeReactJsonSchemaFormData';
import { isUserLoggedIn } from '../../../helpers/userMetadataHelper';
import { reset } from '../../../actions';
import useGetQuestionnaireCountHook from './hooks/useGetQuestionnaireCountHook';
import { STATE__ANONYMOUS } from '../../../constants';

const Content = () => {
  const dispatch = useDispatch();
  // Dane inicjalizacyjne notyfikacji
  const initialNotificationMessage = { value: '', type: null };
  // Dane poczatkowe ankiety (do porównania na końcu)
  const [initialQuestionnaireData, setInitialQuestionnaireData] = useState({});
  // Dane obecne ankiety
  const [questionnaireData, setQuestionnaireData] = useState({});
  // Dane notyfikacyjne (wyswietlanie komunikatów)
  const [notificationMessage, setNotificationMessage] = useState(initialNotificationMessage);
  // Wybrany element menu
  const selectedMenuItem = useSelector((store) => store.authorizedState.get('selectedMenuItem'));
  // Wybrany formularz/ankieta
  const selectedElement = useSelector((store) => store.authorizedState.get('selectedElement'));
  const state = useSelector((store) => store.application.get('state'));
  // Czy wyświetlić panel z ostrzeżeniem przed wyjściem z uzupełniania pól formularza
  const [showConfirmPanel, setShowConfirmPanel] = useState(false);
  // Id wybranego elementu z menu (po potwierdzeniu przerwania uzupełniania wartości)
  const [choosenMenuItem, setChoosenMenuItem] = useState();
  const [refreshQuestionariesTimeStamp, setRefreshQuestionariesTimeStamp] = useState();
  const [confirmPanelData, setConfirmPanelData] = useState();
  const [rawSchemaRequired, setRawSchemaRequired] = useState();

  const isReadOnly = useSelector((store) => store.authorizedState.get('readOnlyElement'));
  // const [formVisualization, setFormVisualization] = useState();
  const formRef = useRef(null);
  // Zabezpieczenie na wypadek wylogowania w innej karcie, aby uzytkownik nie mógł przechodzić pomiedzy elementami menu
  useEffect(() => {
    const availableMenuItemsForLoggedUser = [
      MENU_ITEM__FORMS__ASSIGNED,
      MENU_ITEM__FORMS__DRAFT,
      MENU_ITEM__FORMS__RETURNED,
      MENU_ITEM__FORMS__SENT,
      MENU_ITEM__FORMS__APPROVED,
    ];

    if (selectedMenuItem && availableMenuItemsForLoggedUser.includes(selectedMenuItem) && !isUserLoggedIn()) {
      dispatch(reset());
    }
  }, [selectedMenuItem]);

  // Zdarzenie otrzymania formularza/ankiety z api
  const onReceiveQuestionnaireData = (fields, rawSchema) => {
    const mappedQuestionnaireData = {};
    const mappedInitialQuestionnaireData = initializeReactJsonSchemaFormData(rawSchema);

    if (rawSchema && rawSchema.properties) {
      convertToReactJsonSchemaFormData(selectedMenuItem, rawSchema.properties, null, mappedQuestionnaireData, fields, rawSchema.required);
    }

    setQuestionnaireData(mappedQuestionnaireData);
    setInitialQuestionnaireData(mappedInitialQuestionnaireData);
    setRawSchemaRequired(rawSchema.required);
  };

  // Zdarzenie błędu
  const onError = (message) => {
    setNotificationMessage(message);
    scrollToNearestVerticalPageEdge();
  };

  // Zdarzenie powodzenia
  const onSuccess = (message) => {
    setNotificationMessage({ value: message, type: notificationTypes.SUCCESS });
  };

  // Dane porbanego formularza/ankiety (schema, uiSchema, tytuł)
  const {
    isQuestionnaireFetching,
    questionnaireTitle,
    schema,
    uiSchema,
  } = useGetQuestionnaire(selectedElement, onReceiveQuestionnaireData, onError);
  const { isQuestionnairesFetching, questionnaires } = useGetQuestionnaires(selectedMenuItem, selectedElement, onError, refreshQuestionariesTimeStamp);
  const { sendQuestionnaire, isSending } = useSendQuestionnaire();
  const { saveDraftQuestionnaire, isSavingDraft } = useSaveDraftQuestionnaireHook();
  const { exportQuestionnaire, isExporting } = useExportQuestionnaireHook();
  const {
    assignedQuestionnaireCount,
    returnedQuestionnaireCount,
    sentQuestionnaireCount,
    draftQuestionnaireCount,
    approvedQuestionnaireCount,
    triggerQuestionnaireCount,
    countersFetching,
  } = useGetQuestionnaireCountHook();

  const clearQuestionnaireData = () => {
    setQuestionnaireData({});
    setInitialQuestionnaireData({});
  };

  // Czyszenie notyfikacji i formularza/ankiety przy zmianie wybranego elementu z listy
  useEffect(() => {
    if (selectedElement) {
      setNotificationMessage(initialNotificationMessage);
    }
    clearQuestionnaireData();
  }, [selectedElement]);

  // czyszenie notyfikacji przy zmianie elementu menu
  useEffect(() => {
    setNotificationMessage(initialNotificationMessage);
  }, [selectedMenuItem]);

  const questionnairesData = mapQuestionnaires(questionnaires, selectedMenuItem);

  // zdarzenie sukcesu zapisu wersji roboczej
  const onSaveDraftSuccess = () => {
    dispatch(setSelectedElement(null));
    onSuccess(`Formularz "${questionnaireTitle}" został zapisany.`);
    clearQuestionnaireData();
    triggerQuestionnaireCount();
  };

  // Zdarzenie zapisu wersji roboczej
  const onSaveDraft = () => {
    const draftData = prepareQuestionnaireToSave(questionnaireData, initialQuestionnaireData, schema, uiSchema);

    setShowConfirmPanel(true);
    setConfirmPanelData({
      message: 'Czy na pewno chcesz zapisać formularz?',
      onConfirm: () => {
        setShowConfirmPanel(false);
        saveDraftQuestionnaire(selectedElement, draftData, onSaveDraftSuccess, onError);
      },
      onCancel: () => {
        setShowConfirmPanel(false);
      },
    });
  };

  // Zapis id wysłanej ankiety w localstorage
  const saveSentQuestionnaireIdInLocalStorage = () => {
    const sentQuestionnaires = localStorage.getItem('questionnaires:sent');
    if (sentQuestionnaires) {
      const sentQuestionnairesList = JSON.parse(sentQuestionnaires);

      localStorage.setItem('questionnaires:sent', JSON.stringify([...sentQuestionnairesList, selectedElement]));
    } else {
      localStorage.setItem('questionnaires:sent', JSON.stringify([selectedElement]));
    }
  };

  // Zdarzenie sukcesu wysłanaia formularza/ankiety
  const onQuestionnaireSendSuccess = () => {
    if (selectedMenuItem === MENU_ITEM__QUESTIONNAIRES__AVAILABLE) {
      saveSentQuestionnaireIdInLocalStorage();
    }

    dispatch(setSelectedElement(null));
    onSuccess(`Formularz "${questionnaireTitle}" został wysłany.`);
    clearQuestionnaireData();

    if (state !== STATE__ANONYMOUS) {
      triggerQuestionnaireCount();
    }
  };

  // Zdarzenie wysłania formularza/ankiety
  const onSendQuestionnaire = () => {
    const mappedQuestionnaireData = prepareQuestionnaireToSave(questionnaireData, initialQuestionnaireData, schema, uiSchema);

    setShowConfirmPanel(true);
    setConfirmPanelData({
      message: 'Czy na pewno chcesz zapisać formularz i wysłać do Urzędu?',
      onConfirm: () => {
        setShowConfirmPanel(false);
        sendQuestionnaire(selectedMenuItem, selectedElement, mappedQuestionnaireData, onQuestionnaireSendSuccess, onError);
      },
      onCancel: () => {
        setShowConfirmPanel(false);
      },
    });
  };

  const replaceNodes = (children, parent) => {
    if (children) {
      Array.from(children)?.forEach?.((item) => {
        if (item.tagName === 'INPUT' && item.type === 'text') {
          const node = document.createElement('p');
          const textNode = document.createTextNode(item.value);
          node.appendChild(textNode);
          node.classList.add('valuestyle');
          parent.replaceChild(node, item);
        } else if (item.tagName === 'INPUT' && item.type === 'time') {
          const node = document.createElement('p');
          const textNode = document.createTextNode(item.value);
          node.appendChild(textNode);
          node.classList.add('valuestyle');
          parent.replaceChild(node, item);
        } else if (item.tagName === 'INPUT' && item.type === 'number') {
          const node = document.createElement('p');
          const textNode = document.createTextNode(item.value);
          node.appendChild(textNode);
          node.classList.add('valuestyle');
          parent.replaceChild(node, item);
        } else if (item.tagName === 'TEXTAREA') {
          const node = document.createElement('p');
          const textNode = document.createTextNode(item.value);
          node.appendChild(textNode);
          node.classList.add('textareavaluestyle');
          parent.replaceChild(node, item);
        } else if (item.tagName === 'SELECT' && item.type === 'select-one') {
          const el = item[item.selectedIndex];
          if (el != null) {
            const node = document.createElement('p');
            const textNode = document.createTextNode(el.innerText || '');

            if (textNode !== null) {
              node.appendChild(textNode);
            }
            node.classList.add('valuestyle');
            parent.replaceChild(node, item);
          }
        } else if (item.tagName === 'SELECT' && item.type === 'select-multiple') {
          // const idArray = item.id.split('_');
          // const name = idArray[idArray.length - 1];
          // const el = questionnaireData?.find((x) => x.name === name);
          // if (el != null) {
          //   const node = document.createElement('p');
          //   let textNode;
          //   if (Array.isArray(el.values)) {
          //     const displayValues = el.values.map((val) => {
          //       return val.displayValue;
          //     });

          //     const nodeUl = document.createElement('ul');
          //     displayValues.forEach((v) => {
          //       const nodeLi = document.createElement('li');
          //       textNode = document.createTextNode(v);
          //       nodeLi.appendChild(textNode);
          //       nodeUl.appendChild(nodeLi);
          //     });

          //     node_ul.classList.add('valuestyle');
          //     node.appendChild(nodeUl);
          //   }
          const node = document.createElement('p');
          const textNode = document.createTextNode('');

          if (textNode !== null) {
            node.appendChild(textNode);
          }
          parent.replaceChild(node, item);
          // }
        } else if (item.tagName === 'DIV' && item.className === 'browse-button disabled') {
          parent.removeChild(item);
        } else if (item.children) {
          replaceNodes(item.children, item);
        }
      });
    }
  };

  // Zdarzenie Eksportu formularza/ankiety
  const onExportQuestionnaire = () => {
    const htmlObjectHtml = formRef.current.getHtml();
    const htmlObject = htmlObjectHtml?.current;

    replaceNodes(htmlObject.children, htmlObject);
    const oform = htmlObject.outerHTML;
    const htmlTags = exportHtml.replace('#FORM#', oform);
    const htmlString = encodeURI(htmlTags);
    exportQuestionnaire(selectedElement, htmlString);
  };

  // Anulowanie wypełniania formularza
  const onDeleteQuestionnaire = () => {
    if (!isReadOnly) {
      setShowConfirmPanel(true);
      setConfirmPanelData({
        message: 'Czy na pewno chcesz przerwać wypełnianie formularza bez zapisu?',
        onConfirm: () => {
          setShowConfirmPanel(false);
          triggerQuestionnaireCount();
          dispatch(setSelectedElement(null));
        },
        onCancel: () => {
          setShowConfirmPanel(false);
        },
      });
    } else {
      triggerQuestionnaireCount();
      dispatch(setSelectedElement(null));
    }
  };

  const onQuestionnaireDataChange = (values) => {
    setQuestionnaireData((prevState) => ({
      ...prevState,
      ...values,
    }));
  };

  const changeSelectedMenuItem = (newMenuItem) => {
    if (!isReadOnly) {
      setShowConfirmPanel(true);
      setChoosenMenuItem(newMenuItem);
      setConfirmPanelData({
        message: 'Czy na pewno chcesz przerwać wypełnianie formularza bez zapisu?',
        onConfirm: () => {
          setShowConfirmPanel(false);
          triggerQuestionnaireCount();
          dispatch(setSelectedElement(null));
          dispatch(setSelectedMenuItem(choosenMenuItem));
        },
        onCancel: () => {
          setShowConfirmPanel(false);
        },
      });
    } else {
      setChoosenMenuItem(newMenuItem);
      triggerQuestionnaireCount();
      dispatch(setSelectedElement(null));
      dispatch(setSelectedMenuItem(newMenuItem));
    }
  };

  const refreshMainContent = () => {
    setRefreshQuestionariesTimeStamp((new Date()).toString());
  };

  const questionnaireFields = {
    ArrayField: CustomReactJsonSchemaFormComponents.ArrayField,
    BooleanField: CustomReactJsonSchemaFormComponents.BooleanField,
    NumberField: CustomReactJsonSchemaFormComponents.NumberField,
    ObjectField: CustomReactJsonSchemaFormComponents.ObjectField,
    StringField: CustomReactJsonSchemaFormComponents.StringField,
  };
  const questionnaireWidgets = {
    [CustomReactJsonSchemaFormComponents.AvailableWidgets.AdditionFieldWidget]: CustomReactJsonSchemaFormComponents.AdditionFieldWidget,
    [CustomReactJsonSchemaFormComponents.AvailableWidgets.ArithmeticAverageFieldWidget]: CustomReactJsonSchemaFormComponents.ArithmeticAverageFieldWidget,
    [CustomReactJsonSchemaFormComponents.AvailableWidgets.ArithmeticExpressionFieldWidget]: CustomReactJsonSchemaFormComponents.ArithmeticExpressionFieldWidget,
    [CustomReactJsonSchemaFormComponents.AvailableWidgets.DateFieldWidget]: CustomReactJsonSchemaFormComponents.DateFieldWidget,
    [CustomReactJsonSchemaFormComponents.AvailableWidgets.DateTimeFieldWidget]: CustomReactJsonSchemaFormComponents.DateTimeFieldWidget,
    [CustomReactJsonSchemaFormComponents.AvailableWidgets.DivisionFieldWidget]: CustomReactJsonSchemaFormComponents.DivisionFieldWidget,
    [CustomReactJsonSchemaFormComponents.AvailableWidgets.FileChooserFieldWidget]: CustomReactJsonSchemaFormComponents.FileChooserFieldWidget,
    [CustomReactJsonSchemaFormComponents.AvailableWidgets.MaximumFieldWidget]: CustomReactJsonSchemaFormComponents.MaximumFieldWidget,
    [CustomReactJsonSchemaFormComponents.AvailableWidgets.MinimumFieldWidget]: CustomReactJsonSchemaFormComponents.MinimumFieldWidget,
    [CustomReactJsonSchemaFormComponents.AvailableWidgets.MultiplicationFieldWidget]: CustomReactJsonSchemaFormComponents.MultiplicationFieldWidget,
    [CustomReactJsonSchemaFormComponents.AvailableWidgets.NumericFieldWidget]: CustomReactJsonSchemaFormComponents.NumericFieldWidget,
    [CustomReactJsonSchemaFormComponents.AvailableWidgets.NumericThousandsSeparatorFieldWidget]: CustomReactJsonSchemaFormComponents.NumericThousandsSeparatorFieldWidget,
    [CustomReactJsonSchemaFormComponents.AvailableWidgets.SubtractionFieldWidget]: CustomReactJsonSchemaFormComponents.SubtractionFieldWidget,
    [CustomReactJsonSchemaFormComponents.AvailableWidgets.SumFieldWidget]: CustomReactJsonSchemaFormComponents.SumFieldWidget,
    [CustomReactJsonSchemaFormComponents.AvailableWidgets.TimeFieldWidget]: CustomReactJsonSchemaFormComponents.TimeFieldWidget,
    DictionaryOptionsListWidget: 'SelectWidget',
    DictionaryCheckboxesWidget: 'checkboxes',
  };

  return (
    <div className="content authorized-state-content">
      <Menu
        questionnairesData={questionnairesData}
        assignedQuestionnaireCount={assignedQuestionnaireCount}
        returnedQuestionnaireCount={returnedQuestionnaireCount}
        sentQuestionnaireCount={sentQuestionnaireCount}
        draftQuestionnaireCount={draftQuestionnaireCount}
        approvedQuestionnaireCount={approvedQuestionnaireCount}
        triggerQuestionnaireCount={triggerQuestionnaireCount}
        countersFetching={countersFetching}
        questionnairesFetching={isQuestionnairesFetching}
        changeSelectedMenuItem={(menuItem) => changeSelectedMenuItem(menuItem)}
        refreshMainContent={refreshMainContent}
      />
      <Choose>
        <When condition={selectedElement}>
          <div className="questionnaire-wrapper">
            <If condition={showConfirmPanel}>
              <ConfirmActionPanel
                confirmData={confirmPanelData}
              />
            </If>
            <Questionnaire
              message={notificationMessage}
              isFetching={isQuestionnaireFetching}
              isSavingDraft={isSavingDraft}
              selectedElement={selectedElement}
              title={questionnaireTitle}
              schema={schema}
              uiSchema={uiSchema}
              fields={questionnaireFields}
              widgets={questionnaireWidgets}
              onSaveDraft={onSaveDraft}
              questionnaireData={questionnaireData}
              isSending={isSending}
              isExporting={isExporting}
              setQuestionnaireData={onQuestionnaireDataChange}
              onSendQuestionnaire={onSendQuestionnaire}
              onExportQuestionnaire={onExportQuestionnaire}
              onDeleteQuestionnaire={onDeleteQuestionnaire}
              rawSchemaRequired={rawSchemaRequired}
              ref={formRef}
            />
          </div>
        </When>
        <Otherwise>
          <Questionnaires
            message={notificationMessage}
            items={questionnairesData}
            isFetching={isQuestionnairesFetching}
          />
        </Otherwise>
      </Choose>
    </div>
  );
};

export default Content;
